import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import { ToastStatusEnum } from '@components/ui/toaster';
import { selectActiveUser, selectSessionUuid } from '@redux/authent/authent.selectors';
import {
  selectAxisInvertedY,
  selectLinearJoystickCoefficient,
  selectZoomJoystickCameraSensitivity,
} from '@redux/settings/settings.selectors';
import { selectTrackingStatusByUniqueKey } from '@redux/situation/monitoring.selectors';
import { usePostCameraCommandMutation } from '@services/c2/c2.api';
import { createSensorToast, getSensorUniqueKey, SensorToastParams } from '@utils/sensors/sensors.utils';
import { isEqual } from 'lodash';

import {
  CameraCommand,
  CameraCommandActivateIr,
  CameraCommandDeIcing,
  CameraCommandEnum,
  CameraCommandFocus,
  CameraCommandGetIrConfig,
  CameraCommandHybridFocus,
  CameraCommandLookAt,
  CameraCommandMove,
  CameraCommandNuc,
  CameraCommandPointAt,
  CameraCommandPointAtPixel,
  CameraCommandRebootCharm,
  CameraCommandReleaseToken,
  CameraCommandRequestToken,
  CameraCommandResetIrConfig,
  CameraCommandSensorTracking,
  CameraCommandSetIrConfig,
  CameraCommandSetIso,
  CameraCommandSetPolarity,
  CameraCommandSetWhiteBalance,
  CameraCommandSimpleRound,
  CameraCommandStopAllTrackings,
  CameraCommandSystemMode,
  CameraCommandToggleIrConfig,
  CameraCommandVideoPower,
  CameraCommandVideoTracking,
  CameraCommandZoom,
  CameraCommandZoomSync,
  FocusDirectionEnum,
  Target,
  VideoBox,
  ZoomDirectionEnum,
} from '@/types/c2/c2.types';
import { Coordinates } from '@/types/commons/commons.types';
import { CameraConfiguration, CameraType } from '@/types/sensor/configuration.types';
import { MonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { LocationTypeEnum, SpatialLocation, TargetLocation } from '@/types/sensor/sensor.types';
import {
  CameraIrAlgoEnum,
  IsoModeEnum,
  SystemModeEnum,
  TrackingStatusEnum,
  WhiteBalanceModeEnum,
} from '@/types/sensor/sensors';
import { CameraStatus } from '@/types/sensor/status.types';

import { useAppSelector } from './redux.hooks';

interface UseCameraCommandParameter {
  openedSubCameraId?: number;
  monitoringSensor: MonitoringSensorByType<CameraType> | SensorOrSubSensorInfoByType<CameraType> | null;
  initCameraOperatorUuid?: string | null;
}

export interface UseCameraCommandFunctions {
  zoomStop: () => void;
  zoomIn: (withStop?: boolean) => void;
  zoomOut: (withStop?: boolean) => void;
  setIso: (iso: IsoModeEnum) => void;
  setWhiteBalance: (whiteBalance: WhiteBalanceModeEnum) => void;
  setPolarity: (black: boolean) => void;
  hybridFocus: (focusLevel: number) => void;
  zoomSync: () => void;
  focusIn: () => void;
  focusOut: () => void;
  focusStop: () => void;
  focusAuto: () => void;
  videoTracking: (box: VideoBox | null, mode: TrackingStatusEnum, id: string | null) => void;
  videoBuffering: () => void;
  videoScanning: () => void;
  move: (speed: { panSpeed: number; tiltSpeed: number }) => void;
  simpleRound: (active: boolean, speed?: number) => void;
  moveJoystick: (dx: number, dy: number) => void;
  isLoading: false | true;
  isError: false | true;
  requestToken: (configuration?: CameraConfiguration) => Promise<void>;
  sensorTracking: (target: Target<TargetLocation> | null, force: boolean, configuration?: CameraConfiguration) => void;
  stopAllTrackings: (target: Target<TargetLocation> | null, force: boolean) => void;
  lookAt: (
    position: Coordinates,
    segmentStart: Coordinates | null,
    segmentEnd: Coordinates | null,
    altitude: number | null,
    objectSize: number | null,
    force: boolean,
    configuration?: CameraConfiguration,
  ) => void;
  pointAt: (x: number, y: number, width: number, height: number) => void;
  pointAtPixel: (imageWidth: number, imageHeight: number, clickPositionX: number, clickPositionY: number) => void;
  releaseToken: (configuration?: CameraConfiguration) => void;
  rebootCharm: () => void;
  canCommand: boolean;
  isSubCameraAvailable: boolean;
  deIcing: (on: boolean) => void;
  nuc: () => void;
  factoryPosition: () => void;
  parkingPosition: () => void;
  videoPower: (on: boolean, cameraId: number, configuration?: CameraConfiguration) => void;
  getIrConfig: (cameraId: number, configuration?: CameraConfiguration) => void;
  setIrConfig: (
    gain: number | null,
    offset: number | null,
    gamma: number | null,
    cameraId: number,
    configuration?: CameraConfiguration,
  ) => void;
  resetIrConfig: (cameraId: number, configuration?: CameraConfiguration) => void;
  toggleIrConfig: (algo: CameraIrAlgoEnum, cameraId: number, configuration?: CameraConfiguration) => void;
  activateIr: (on: boolean) => void;
}

export const useCameraCommand: (params: UseCameraCommandParameter) => UseCameraCommandFunctions = ({
  monitoringSensor,
  openedSubCameraId,
}: UseCameraCommandParameter) => {
  const [postCameraCommand, { isError, isLoading }] = usePostCameraCommandMutation();
  const cameraStatus = monitoringSensor?.status ?? null;
  const cameraConfiguration = monitoringSensor?.configuration ?? null;
  const user = useAppSelector(selectActiveUser);
  const sessionUuid = useAppSelector(selectSessionUuid);
  const axisInvertedY = useAppSelector(selectAxisInvertedY);
  const linearJoystickCoefficient = useAppSelector(selectLinearJoystickCoefficient);
  const zoomJoystickCameraSensitivity = useAppSelector(selectZoomJoystickCameraSensitivity);
  const hasControl = cameraStatus?.cameraToken?.cameraOperatorUuid === sessionUuid;
  const isSubCameraAvailable = !!cameraStatus?.videoStatuses.some((status) => status.id === openedSubCameraId);
  const site = user.activeSite?.code ?? '';
  const appCode = cameraConfiguration?.appCode ?? '';
  const cameraCode = cameraConfiguration?.code ?? '';
  const cameraName = cameraConfiguration?.name ?? '';
  const trackingStateOfSelectedCamera = useAppSelector(
    (state) => selectTrackingStatusByUniqueKey(state, false, cameraStatus ? getSensorUniqueKey(cameraStatus) : null),
    isEqual,
  );
  const trackingStateOfSelectedFlux =
    openedSubCameraId !== undefined && trackingStateOfSelectedCamera?.videoTrackingStatus.holopticsVideoTrackings
      ? trackingStateOfSelectedCamera.videoTrackingStatus.holopticsVideoTrackings[openedSubCameraId]?.status
      : null;

  const isBufferized =
    openedSubCameraId !== undefined && trackingStateOfSelectedCamera?.videoTrackingStatus.holopticsVideoTrackings
      ? trackingStateOfSelectedCamera.videoTrackingStatus.holopticsVideoTrackings[openedSubCameraId]?.isBufferized
      : false;

  const requestToken = async (configuration?: CameraConfiguration): Promise<void> => {
    const command: CameraCommandRequestToken = {
      type: CameraCommandEnum.CAMERA_COMMAND_REQUEST_TOKEN,
      sensorCode: configuration?.code ?? cameraCode,
      cameraOperatorUuid: sessionUuid,
      userName: user.name,
      userLogin: user.login,
    };
    const toastParams = {
      command: 'sensors.commands.camera.requestTokenCommand',
      name: cameraName,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    return postCameraCommand({
      command,
      site: configuration?.site ?? site,
      appCode: configuration?.appCode ?? appCode,
    })
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  };

  const releaseToken = (configuration?: CameraConfiguration) => {
    const command: CameraCommandReleaseToken = {
      type: CameraCommandEnum.CAMERA_COMMAND_RELEASE_TOKEN,
      sensorCode: configuration?.code ?? cameraCode,
      cameraOperatorUuid: sessionUuid,
      userLogin: user.login,
    };
    const toastParams = {
      command: 'sensors.commands.camera.releaseTokenCommand',
      name: cameraName,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    postCameraCommand({
      command,
      site: configuration?.site ?? site,
      appCode: configuration?.appCode ?? appCode,
    })
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  };

  async function sendCommand(
    command: CameraCommand,
    toastParams?: SensorToastParams,
    force?: boolean,
    configuration?: CameraConfiguration,
    cameraStatus?: CameraStatus,
  ) {
    const canControl = cameraStatus ? cameraStatus.cameraToken?.cameraOperatorUuid === sessionUuid : hasControl;
    const fullCommand = {
      site: configuration?.site ?? site,
      appCode: configuration?.appCode ?? appCode,
      command,
    };
    if (force && !canControl) {
      return requestToken(configuration).then(() => {
        toastParams &&
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.INFO,
          });
        postCameraCommand(fullCommand)
          .unwrap()
          .then(
            () =>
              toastParams &&
              createSensorToast({
                ...toastParams,
                toast: ToastStatusEnum.SUCCESS,
              }),
          )
          .catch(
            () =>
              toastParams &&
              createSensorToast({
                ...toastParams,
                toast: ToastStatusEnum.ERROR,
              }),
          );
      });
    } else if (canControl) {
      toastParams &&
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.INFO,
        });
      return postCameraCommand(fullCommand)
        .unwrap()
        .then(
          () =>
            toastParams &&
            createSensorToast({
              ...toastParams,
              toast: ToastStatusEnum.SUCCESS,
            }),
        )
        .catch(
          () =>
            toastParams &&
            createSensorToast({
              ...toastParams,
              toast: ToastStatusEnum.ERROR,
            }),
        );
    }
  }

  const adjustSpeedForZoom = (joystickTilt: number) => {
    //TODO get right videoStatus HLF-329
    if (isNaN(joystickTilt)) {
      return 0;
    }
    const fov = cameraStatus?.videoStatuses.find((st) => st.id === openedSubCameraId)?.fov;
    const fovMax = cameraConfiguration?.subCameras.find((sc) => sc.cameraId === openedSubCameraId)?.maxViewAngle;
    if (fov && fovMax && joystickTilt && openedSubCameraId !== undefined) {
      //Use max speed and max fov to ensure a constant time to travel current fov
      const maxSpeedForCurrentZoom = fov / fovMax;
      const maxSpeedForCurrentZoomAndSensitivity =
        maxSpeedForCurrentZoom + (1 - zoomJoystickCameraSensitivity) * (1 - maxSpeedForCurrentZoom);
      return joystickTilt * maxSpeedForCurrentZoomAndSensitivity;
    }
    return joystickTilt;
  };

  const simpleRound = (active: boolean, speed?: number): void => {
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_SIMPLE_ROUND,
        cameraOperatorUuid: sessionUuid,
        sensorCode: cameraCode,
        roundOn: active,
        speed: speed,
      } as CameraCommandSimpleRound,
      {
        commandValues: { state: active },
        command: 'sensors.commands.camera.simpleRoundCommand',
        name: cameraName,
      },
    ).then();
  };

  const move = (speed: { panSpeed: number; tiltSpeed: number }) => {
    sendCommand({
      type: CameraCommandEnum.CAMERA_COMMAND_MOVE,
      cameraOperatorUuid: sessionUuid,
      sensorCode: cameraCode,
      panSpeed: speed.panSpeed,
      tiltSpeed: axisInvertedY ? -speed.tiltSpeed : speed.tiltSpeed,
    } as CameraCommandMove).then();
  };

  const setIso = (iso: IsoModeEnum) => {
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_ISO_MODE,
        cameraOperatorUuid: sessionUuid,
        sensorCode: cameraCode,
        cameraId: openedSubCameraId,
        iso,
      } as CameraCommandSetIso,
      {
        commandValues: { state: iso },
        command: 'sensors.commands.camera.isoCommand',
        name: cameraName,
      },
    ).then();
  };

  const setWhiteBalance = (whiteBalance: WhiteBalanceModeEnum) => {
    sendCommand({
      type: CameraCommandEnum.CAMERA_COMMAND_WHITER_BALANCE,
      cameraOperatorUuid: sessionUuid,
      sensorCode: cameraCode,
      cameraId: openedSubCameraId,
      whiteBalance,
    } as CameraCommandSetWhiteBalance).then();
  };

  const hybridFocus = (focusLevel: number) => {
    sendCommand({
      type: CameraCommandEnum.CAMERA_COMMAND_HYBRID_FOCUS,
      cameraOperatorUuid: sessionUuid,
      sensorCode: cameraCode,
      cameraId: openedSubCameraId,
      focusLevel,
    } as CameraCommandHybridFocus).then();
  };

  const setPolarity = (black: boolean) => {
    sendCommand({
      type: CameraCommandEnum.CAMERA_COMMAND_SET_POLARITY,
      cameraOperatorUuid: sessionUuid,
      sensorCode: cameraCode,
      cameraId: openedSubCameraId,
      black: black,
    } as CameraCommandSetPolarity).then();
  };

  function calculateSpeed(dx: number, minSpeed: number, maxSpeed: number): number {
    if (dx === 0) {
      return 0;
    }
    // Configuration of the joystick continuous between linear and cubic
    // depending on the linear joystick coefficient
    // Explanation :
    // a linear joystick coefficient
    // Joystick configuration is corresponding to h(\x) = a*x + (1-a)*x^3
    let x: number = +(linearJoystickCoefficient * dx + (1 - linearJoystickCoefficient) * Math.pow(dx, 3));
    x = +adjustSpeedForZoom(x).toFixed(2);

    return Math.abs(x) < minSpeed ? Math.sign(dx) * minSpeed : x * maxSpeed;
  }

  const moveJoystick = (dx: number, dy: number) => {
    if (!hasControl || !cameraConfiguration) {
      return;
    }

    const vxMin = cameraConfiguration.vxMin;
    const vxMax = cameraConfiguration.vxMax;
    const vyMin = cameraConfiguration.vyMin;
    const vyMax = cameraConfiguration.vyMax;

    const panSpeed: number = calculateSpeed(dx, vxMin, vxMax);
    const tiltSpeed: number = calculateSpeed(dy, vyMin, vyMax);

    move({ panSpeed: panSpeed, tiltSpeed: tiltSpeed });
  };

  const onZoom = (direction: ZoomDirectionEnum) => (withStop?: boolean) => {
    if (hasControl && openedSubCameraId !== undefined) {
      sendCommand({
        type: CameraCommandEnum.CAMERA_COMMAND_ZOOM,
        cameraOperatorUuid: sessionUuid,
        sensorCode: cameraCode,
        direction: direction,
        cameraId: openedSubCameraId,
      } as CameraCommandZoom).then(() => withStop && onZoom(ZoomDirectionEnum.STOP));
    }
  };

  const zoomSync = () => {
    if (hasControl) {
      const status = cameraStatus.videoStatuses.find((video) => video.id === openedSubCameraId);
      if (status) {
        sendCommand({
          type: CameraCommandEnum.CAMERA_COMMAND_ZOOM_SYNC,
          cameraOperatorUuid: sessionUuid,
          sensorCode: cameraCode,
          zoom: status.zoom,
          fov: status.fov,
          cameraId: openedSubCameraId,
        } as CameraCommandZoomSync).then();
      }
    }
  };
  const onFocus = (direction: FocusDirectionEnum) => () => {
    if (hasControl && openedSubCameraId !== undefined) {
      sendCommand({
        type: CameraCommandEnum.CAMERA_COMMAND_FOCUS,
        sensorCode: cameraCode,
        cameraOperatorUuid: sessionUuid,
        direction,
        cameraId: openedSubCameraId,
      } as CameraCommandFocus).then();
    }
  };

  const sensorTracking = (
    target: Target<TargetLocation> | null,
    force: boolean,
    configuration?: CameraConfiguration,
  ) => {
    const type = target?.lastPosition.location?.type === LocationTypeEnum.SPATIAL ? '3D' : '2D';
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_SENSOR_TRACKING,
        sensorCode: configuration?.code ?? cameraCode,
        cameraOperatorUuid: sessionUuid,
        trackedTargetId: target?.id ?? null,
        targetFirstPosition: {
          type: type,
          longitude: target?.lastPosition.location?.position?.longitude,
          latitude: target?.lastPosition.location?.position?.latitude,
          altitude: type === '2D' ? null : (target?.lastPosition.location as SpatialLocation).altitude,
        },
      } as CameraCommandSensorTracking,
      {
        commandValues: { state: target !== null },
        command: 'sensors.commands.camera.sensorTrackingCommand',
        name: cameraName,
      },
      force,
      configuration,
    ).then(() => onFocus(FocusDirectionEnum.AUTO_FOCUS)());
  };

  const videoPower = (on: boolean, cameraId: number, configuration?: CameraConfiguration) => {
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_VIDEO_POWER,
        sensorCode: configuration?.code ?? cameraCode,
        cameraOperatorUuid: sessionUuid,
        on,
        cameraId,
      } as CameraCommandVideoPower,
      {
        commandValues: { state: on },
        command: 'sensors.commands.camera.videoPowerCommand',
        name: cameraName,
      },
      true,
      configuration,
    ).then();
  };

  const getIrConfig = (cameraId: number, configuration?: CameraConfiguration) => {
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_GET_IR_CONFIG,
        sensorCode: configuration?.code ?? cameraCode,
        cameraOperatorUuid: sessionUuid,
        cameraId,
      } as CameraCommandGetIrConfig,
      {
        command: 'sensors.commands.camera.getIrConfig',
        name: cameraName,
      },
      false,
      configuration,
    ).then();
  };

  const setIrConfig = (
    gain: number | null,
    offset: number | null,
    gamma: number | null,
    cameraId: number,
    configuration?: CameraConfiguration,
  ) => {
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_SET_IR_CONFIG,
        sensorCode: configuration?.code ?? cameraCode,
        cameraOperatorUuid: sessionUuid,
        cameraId,
        gain,
        offset,
        gamma,
      } as CameraCommandSetIrConfig,
      {
        command: 'sensors.commands.camera.setIrConfig',
        name: cameraName,
      },
      false,
      configuration,
    ).then();
  };

  const resetIrConfig = (cameraId: number, configuration?: CameraConfiguration) => {
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_RESET_IR_CONFIG,
        sensorCode: configuration?.code ?? cameraCode,
        cameraOperatorUuid: sessionUuid,
        cameraId,
      } as CameraCommandResetIrConfig,
      {
        command: 'sensors.commands.camera.resetIrConfig',
        name: cameraName,
      },
      false,
      configuration,
    ).then();
  };

  const toggleIrConfig = (algo: CameraIrAlgoEnum, cameraId: number, configuration?: CameraConfiguration) => {
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_TOGGLE_IR_CONFIG,
        sensorCode: configuration?.code ?? cameraCode,
        cameraOperatorUuid: sessionUuid,
        cameraId,
        algo,
      } as CameraCommandToggleIrConfig,
      {
        command: 'sensors.commands.camera.toggleIrConfig',
        name: cameraName,
      },
      false,
      configuration,
    ).then();
  };

  const activateIr = (on: boolean) => {
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_ACTIVATE_IR,
        cameraOperatorUuid: sessionUuid,
        sensorCode: cameraCode,
        cameraId: openedSubCameraId,
        on,
      } as CameraCommandActivateIr,
      {
        command: 'sensors.commands.camera.activateIr',
        name: cameraName,
      },
    ).then();
  };

  const stopAllTrackings = (target: Target<TargetLocation> | null, force: boolean) => {
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_STOP_ALL_TRACKINGS,
        cameraOperatorUuid: sessionUuid,
        trackedTargetId: target?.id ?? null,
      } as CameraCommandStopAllTrackings,
      {
        command: 'sensors.commands.camera.stopAllTrackingCommand',
        name: cameraName,
      },
      force,
    ).then();
  };

  const lookAt = (
    position: Coordinates,
    segmentStart: Coordinates | null,
    segmentEnd: Coordinates | null,
    altitude: number | null,
    objectSize: number | null,
    force: boolean,
    configuration?: CameraConfiguration,
  ) => {
    const segments = segmentStart && segmentEnd ? { segmentStart, segmentEnd } : null;
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_LOOK_AT,
        sensorCode: configuration?.code ?? cameraCode,
        cameraOperatorUuid: sessionUuid,
        position: position,
        segmentStart: segments?.segmentStart,
        segmentEnd: segments?.segmentEnd,
        objectSize,
        altitude,
      } as CameraCommandLookAt,
      undefined,
      force,
      configuration,
    ).then(() => onFocus(FocusDirectionEnum.AUTO_FOCUS)());
  };

  const pointAt = (x: number, y: number, width: number, height: number) => {
    if (hasControl && openedSubCameraId !== undefined)
      sendCommand({
        type: CameraCommandEnum.CAMERA_COMMAND_POINT_AT,
        sensorCode: cameraConfiguration?.code ?? cameraCode,
        cameraOperatorUuid: sessionUuid,
        x,
        y,
        width,
        height,
        cameraId: openedSubCameraId,
      } as CameraCommandPointAt).then();
  };

  const pointAtPixel = (imageWidth: number, imageHeight: number, clickPositionX: number, clickPositionY: number) => {
    sendCommand({
      type: CameraCommandEnum.CAMERA_COMMAND_POINT_AT_PIXEL,
      sensorCode: cameraConfiguration?.code ?? cameraCode,
      cameraOperatorUuid: sessionUuid,
      imageWidth,
      imageHeight,
      clickPositionX,
      clickPositionY,
    } as CameraCommandPointAtPixel).then();
  };

  const videoTracking = (box: VideoBox | null, mode: TrackingStatusEnum, id: string | null) => {
    if (!hasControl) {
      return;
    }
    sendCommand(
      {
        type: CameraCommandEnum.CAMERA_COMMAND_VIDEO_TRACKING,
        sensorCode: cameraCode,
        cameraOperatorUuid: sessionUuid,
        box,
        mode,
        id,
        cameraId: openedSubCameraId,
      } as CameraCommandVideoTracking,
      {
        command: 'sensors.commands.camera.videoTrackingCommand',
        name: cameraName,
      },
    ).then();
  };

  const rebootCharm = () => {
    const toastParams = {
      command: 'sensors.commands.camera.rebootCharmCommand',
      name: cameraName,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    postCameraCommand({
      command: {
        type: CameraCommandEnum.CAMERA_COMMAND_REBOOT_CHARM,
        sensorCode: cameraCode,
        cameraOperatorUuid: sessionUuid,
      } as CameraCommandRebootCharm,
      site,
      appCode,
    })
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  };

  const videoBuffering = () => {
    const cameraUniqueKey = cameraStatus ? getSensorUniqueKey(cameraConfiguration) : null;
    if (!hasControl || openedSubCameraId === undefined || !cameraUniqueKey) {
      return;
    }

    if (!isBufferized) {
      videoTracking(null, TrackingStatusEnum.PENDING, null);
    } else if (trackingStateOfSelectedFlux === TrackingStatusEnum.ACTIVE) {
      videoTracking(null, TrackingStatusEnum.DISABLED, null);
    } else {
      videoTracking(null, TrackingStatusEnum.STOP_BUFFERIZE, null);
    }
  };

  const videoScanning = () => {
    const cameraUniqueCode = cameraStatus ? getSensorUniqueKey(cameraConfiguration) : null;
    if (!hasControl || openedSubCameraId === undefined || !cameraUniqueCode) {
      return;
    }

    if (
      trackingStateOfSelectedFlux === null ||
      trackingStateOfSelectedFlux === undefined ||
      trackingStateOfSelectedFlux === TrackingStatusEnum.DISABLED
    ) {
      videoTracking(null, TrackingStatusEnum.SCANNING, null);
    } else if (trackingStateOfSelectedFlux === TrackingStatusEnum.ACTIVE) {
      videoTracking(null, TrackingStatusEnum.SCANNING, null);
    } else if (trackingStateOfSelectedFlux === TrackingStatusEnum.SCANNING) {
      videoTracking(null, TrackingStatusEnum.STOP_SCANNING, null);
    }
  };

  const deIcing = (on: boolean) => {
    sendCommand({
      type: CameraCommandEnum.CAMERA_COMMAND_DEICING,
      cameraOperatorUuid: sessionUuid,
      sensorCode: cameraCode,
      cameraId: openedSubCameraId,
      on: on,
    } as CameraCommandDeIcing).then();
  };

  const nuc = () => {
    sendCommand({
      type: CameraCommandEnum.CAMERA_COMMAND_NUC,
      cameraOperatorUuid: sessionUuid,
      sensorCode: cameraCode,
      cameraId: openedSubCameraId,
    } as CameraCommandNuc).then();
  };

  const factoryPosition = () => {
    sendCommand({
      type: CameraCommandEnum.CAMERA_COMMAND_SYSTEM_MODE,
      cameraOperatorUuid: sessionUuid,
      sensorCode: cameraCode,
      systemMode: SystemModeEnum.FACTORY,
    } as CameraCommandSystemMode).then();
  };

  const parkingPosition = () => {
    sendCommand({
      type: CameraCommandEnum.CAMERA_COMMAND_SYSTEM_MODE,
      cameraOperatorUuid: sessionUuid,
      sensorCode: cameraCode,
      systemMode: SystemModeEnum.PARK,
    } as CameraCommandSystemMode).then();
  };

  return {
    zoomIn: onZoom(ZoomDirectionEnum.IN),
    zoomOut: onZoom(ZoomDirectionEnum.OUT),
    zoomStop: onZoom(ZoomDirectionEnum.STOP),
    focusIn: onFocus(FocusDirectionEnum.IN),
    zoomSync,
    focusOut: onFocus(FocusDirectionEnum.OUT),
    focusStop: onFocus(FocusDirectionEnum.STOP),
    focusAuto: onFocus(FocusDirectionEnum.AUTO_FOCUS),
    setIso,
    setWhiteBalance,
    setPolarity,
    hybridFocus,
    move: move,
    simpleRound,
    moveJoystick,
    requestToken,
    sensorTracking,
    videoTracking,
    videoBuffering,
    videoScanning,
    stopAllTrackings,
    lookAt,
    pointAt,
    pointAtPixel,
    releaseToken,
    rebootCharm,
    deIcing,
    nuc,
    isLoading: isLoading,
    isError: isError,
    canCommand: hasControl,
    isSubCameraAvailable,
    factoryPosition,
    parkingPosition,
    videoPower,
    getIrConfig,
    setIrConfig,
    resetIrConfig,
    toggleIrConfig,
    activateIr,
  };
};
